import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { apiRequest } from '@/lib/queryClient';
import {
  ANALYTICS_CONSENT_EVENT,
  getAnalyticsConsent,
  type AnalyticsConsent,
} from '@/lib/analytics';

// Types
export type EventType = 'view' | 'reaction' | 'search' | 'follow' | 'share' | 'audioPlay' | 'scrollDepth' | 'timeSpent';
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface PersonalizationEvent {
  type: EventType;
  contentType: string;
  contentId: string | number;
  timestamp: number;
  sessionId: string;
  deviceType: DeviceType;
  metadata?: Record<string, unknown>;
}

interface PersonalizationTrackerContextValue {
  trackView: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackReaction: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackSearch: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackFollow: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackShare: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackAudioPlay: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackScrollDepth: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  trackTimeSpent: (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => void;
  sessionId: string;
  deviceType: DeviceType;
}

const PersonalizationTrackerContext = createContext<PersonalizationTrackerContextValue | null>(null);

// Utility functions
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function getOrCreateSessionId(): string {
  try {
    const existingSessionId = localStorage.getItem('personalization_session_id');
    if (existingSessionId) return existingSessionId;
    const newSessionId = generateSessionId();
    localStorage.setItem('personalization_session_id', newSessionId);
    return newSessionId;
  } catch {
    return '';
  }
}

function detectDeviceType(): DeviceType {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

// Provider Component
interface PersonalizationTrackerProviderProps {
  children: ReactNode;
}

export function PersonalizationTrackerProvider({ children }: PersonalizationTrackerProviderProps) {
  const [analyticsAllowed, setAnalyticsAllowed] = useState(() => getAnalyticsConsent() === 'granted');
  const [sessionId, setSessionId] = useState(() =>
    getAnalyticsConsent() === 'granted' ? getOrCreateSessionId() : '',
  );
  const [deviceType, setDeviceType] = useState<DeviceType>(() => detectDeviceType());
  const eventBufferRef = useRef<PersonalizationEvent[]>([]);
  const viewDeduplicationRef = useRef<Map<string, number>>(new Map());
  const flushIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const consentGenerationRef = useRef(0);
  const analyticsAllowedRef = useRef(analyticsAllowed);
  const sessionIdRef = useRef(sessionId);
  const flushAbortControllersRef = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    const handleConsentChange = (event: Event) => {
      const consent = (event as CustomEvent<AnalyticsConsent>).detail;
      const allowed = consent === 'granted';
      consentGenerationRef.current += 1;
      analyticsAllowedRef.current = allowed;
      setAnalyticsAllowed(allowed);
      if (allowed) {
        const nextSessionId = sessionIdRef.current || getOrCreateSessionId();
        sessionIdRef.current = nextSessionId;
        setSessionId(nextSessionId);
        return;
      }
      flushAbortControllersRef.current.forEach((controller) => controller.abort());
      flushAbortControllersRef.current.clear();
      eventBufferRef.current = [];
      viewDeduplicationRef.current.clear();
      sessionIdRef.current = '';
      setSessionId('');
      try {
        localStorage.removeItem('personalization_session_id');
      } catch {
        // Storage may be unavailable; tracking remains disabled in memory.
      }
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, handleConsentChange);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, handleConsentChange);
  }, []);

  // Update device type on resize
  useEffect(() => {
    const handleResize = () => {
      setDeviceType(detectDeviceType());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Flush events to API
  const flushEvents = useCallback(async () => {
    if (!analyticsAllowed || !sessionId || eventBufferRef.current.length === 0) return;

    const eventsToSend = [...eventBufferRef.current];
    eventBufferRef.current = [];
    const generation = consentGenerationRef.current;
    const requestSessionId = sessionId;
    const controller = new AbortController();
    flushAbortControllersRef.current.add(controller);

    try {
      await apiRequest('POST', '/api/personalization/events', {
        events: eventsToSend,
        sessionId: requestSessionId,
      }, { signal: controller.signal });
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error('[Personalization] Failed to flush events:', error);
      if (
        analyticsAllowedRef.current
        && sessionIdRef.current === requestSessionId
        && consentGenerationRef.current === generation
      ) {
        eventBufferRef.current.unshift(...eventsToSend);
      }
    } finally {
      flushAbortControllersRef.current.delete(controller);
    }
  }, [analyticsAllowed, sessionId]);

  // Flush events using sendBeacon for reliable delivery on page unload
  const flushEventsSync = useCallback(() => {
    if (!analyticsAllowed || !sessionId || eventBufferRef.current.length === 0) return;

    const eventsToSend = [...eventBufferRef.current];
    eventBufferRef.current = [];


    const payload = JSON.stringify({
      events: eventsToSend,
      sessionId,
    });

    // Use sendBeacon for reliable delivery during page unload
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon('/api/personalization/events', blob);
    } else {
      eventBufferRef.current.unshift(...eventsToSend);
    }
  }, [analyticsAllowed, sessionId]);

  // Setup flush interval
  useEffect(() => {
    if (!analyticsAllowed || !sessionId) return;
    flushIntervalRef.current = setInterval(() => {
      flushEvents();
    }, 5000); // 5 seconds

    return () => {
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
      }
    };
  }, [analyticsAllowed, flushEvents, sessionId]);

  // Flush on page unload using sendBeacon for reliability
  useEffect(() => {
    if (!analyticsAllowed || !sessionId) return;
    const handleBeforeUnload = () => {
      flushEventsSync();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushEventsSync();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [analyticsAllowed, flushEventsSync, sessionId]);

  const trackEvent = useCallback(
    (type: EventType, contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      if (!analyticsAllowed || !sessionId) return;
      // Deduplication for view events
      if (type === 'view') {
        const deduplicationKey = `${contentType}:${contentId}`;
        const lastTrackTime = viewDeduplicationRef.current.get(deduplicationKey);
        const now = Date.now();

        if (lastTrackTime && now - lastTrackTime < 30000) {
          // Skip if tracked within last 30 seconds
          return;
        }

        viewDeduplicationRef.current.set(deduplicationKey, now);
      }

      const event: PersonalizationEvent = {
        type,
        contentType,
        contentId,
        timestamp: Date.now(),
        sessionId,
        deviceType,
        metadata,
      };

      eventBufferRef.current.push(event);

      // Flush immediately for view events (most important) or if buffer reaches 20 events
      if (type === 'view' || eventBufferRef.current.length >= 20) {
        flushEvents();
      }
    },
    [analyticsAllowed, sessionId, deviceType, flushEvents]
  );

  const trackView = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('view', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackReaction = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('reaction', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackSearch = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('search', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackFollow = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('follow', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackShare = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('share', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackAudioPlay = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('audioPlay', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackScrollDepth = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('scrollDepth', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const trackTimeSpent = useCallback(
    (contentType: string, contentId: string | number, metadata?: Record<string, unknown>) => {
      trackEvent('timeSpent', contentType, contentId, metadata);
    },
    [trackEvent]
  );

  const value: PersonalizationTrackerContextValue = {
    trackView,
    trackReaction,
    trackSearch,
    trackFollow,
    trackShare,
    trackAudioPlay,
    trackScrollDepth,
    trackTimeSpent,
    sessionId,
    deviceType,
  };

  return (
    <PersonalizationTrackerContext.Provider value={value} data-testid="personalization-tracker-provider">
      {children}
    </PersonalizationTrackerContext.Provider>
  );
}

// Hook
export function usePersonalizationTracker(): PersonalizationTrackerContextValue {
  const context = useContext(PersonalizationTrackerContext);
  if (!context) {
    throw new Error('usePersonalizationTracker must be used within PersonalizationTrackerProvider');
  }
  return context;
}
